import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/format";

export function CartDrawer() {
  const { items, drawerOpen, setDrawerOpen, updateQty, removeItem, subtotal, count } = useCart();
  const nav = useNavigate();

  const go = (path) => {
    setDrawerOpen(false);
    nav(path);
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        className="bg-obsidian border-l border-gold/30 text-cream w-full sm:max-w-md flex flex-col p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="p-5 border-b border-gold/20">
          <SheetTitle className="font-serif text-gold text-2xl flex items-center gap-2">
            <ShoppingBag size={22} /> Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
            <ShoppingBag size={48} className="text-gold/40" />
            <p className="text-cream/60">Your cart is empty.</p>
            <Button className="btn-gold rounded-full" onClick={() => go("/shop")} data-testid="cart-empty-shop-btn">
              Start Shopping
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-4">
              {items.map((it) => (
                <div key={it.key} className="flex gap-3 gold-border-card rounded-xl p-3 bg-card-surface" data-testid={`cart-item-${it.key}`}>
                  <img src={it.image} alt={it.name} className="h-20 w-20 rounded-lg object-cover border border-gold/20" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-cream text-sm font-semibold truncate">{it.name}</p>
                    <p className="text-cream/60 text-xs mt-0.5">{it.size_label}</p>
                    <p className="font-mono text-gold text-sm mt-1">{money(it.unit_price)}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQty(it.key, it.quantity - 1)} className="h-7 w-7 rounded-full border border-gold/40 flex items-center justify-center hover:bg-gold/10" data-testid={`cart-dec-${it.key}`}>
                          <Minus size={13} />
                        </button>
                        <span className="font-mono w-6 text-center text-sm">{it.quantity}</span>
                        <button onClick={() => updateQty(it.key, it.quantity + 1)} className="h-7 w-7 rounded-full border border-gold/40 flex items-center justify-center hover:bg-gold/10" data-testid={`cart-inc-${it.key}`}>
                          <Plus size={13} />
                        </button>
                      </div>
                      <button onClick={() => removeItem(it.key)} className="text-cream/40 hover:text-destructive" data-testid={`cart-remove-${it.key}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-gold/20 p-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-cream/70">Subtotal</span>
                <span className="font-mono text-gold text-lg">{money(subtotal)}</span>
              </div>
              <p className="text-cream/40 text-xs">Delivery fees & discounts calculated at checkout.</p>
              <Button className="btn-gold w-full rounded-full py-6 text-base font-semibold" onClick={() => go("/checkout")} data-testid="cart-drawer-checkout-btn">
                Checkout
              </Button>
              <Button variant="outline" className="w-full rounded-full border-gold/40 text-cream bg-transparent hover:bg-gold/10" onClick={() => go("/shop")} data-testid="cart-drawer-continue-btn">
                Continue Shopping
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
